import { useState } from 'react';

// Modale d'infos + actions sur le compte d'un contact antenne.
// Pas de popup de confirmation (on est déjà dans une modale) : la sélection d'une action
// affiche un avertissement + un commentaire optionnel, puis un bouton Confirmer.
const GW = (process.env.REACT_APP_GATEWAY_URL || '').replace(/\/+$/, '');
const ETAT_LABEL = { actif: 'Actif', inactif: 'Désactivé', supprime: 'Supprimé' };
const ACTIONS = {
  reset: { label: 'Réinitialiser le mot de passe', warn: 'Un nouveau mot de passe temporaire sera généré et envoyé par email. L\'utilisateur devra le redéfinir à sa prochaine connexion.', color: '#2563eb' },
  block: { label: 'Désactiver le compte', warn: 'Le compte sera désactivé : l\'utilisateur ne pourra plus se connecter.', color: '#d97706' },
  unblock: { label: 'Réactiver le compte', warn: 'Le compte sera réactivé : l\'utilisateur pourra de nouveau se connecter.', color: '#059669' },
  delete: { label: 'Supprimer le compte', warn: 'Le compte sera marqué « supprimé » (changement de statut, pas de suppression réelle) et l\'utilisateur ne pourra plus se connecter.', color: '#dc2626' },
};
const fmt = (d) => (d ? new Date(d).toLocaleString('fr-FR') : null);

export default function ContactAccountModal({ account, token, onChanged, onClose }) {
  const [action, setAction] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [resetCreds, setResetCreds] = useState(null);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const opts = [];
  if (account.etat === 'supprime') opts.push('unblock');
  if (account.etat === 'actif') opts.push('block');
  if (account.etat === 'inactif') opts.push('unblock');
  opts.push('reset');
  if (account.etat !== 'supprime') opts.push('delete');

  const confirm = async () => {
    if (!action) return;
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${GW}/api/user-contact-antennes/account/${account.user_id}`, { method: 'PATCH', headers, body: JSON.stringify({ action, comment }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || ('HTTP ' + r.status));
      if (action === 'reset') setResetCreds(d.data);
      setAction(''); setComment('');
      onChanged && onChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const row = { display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f1f5f9', fontSize: 14 };
  const sel = { width: '100%', padding: 10, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, marginTop: 6 };

  return (
    <div className="modal-overlay" onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 480, background: 'white', borderRadius: 14, padding: 22, maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Compte de {account.prenom} {account.nom}</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 22, cursor: 'pointer', color: '#94a3b8' }}>&times;</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={row}><span style={{ color: '#64748b' }}>Nom</span><b>{account.nom || '—'}</b></div>
          <div style={row}><span style={{ color: '#64748b' }}>Prénom</span><b>{account.prenom || '—'}</b></div>
          <div style={row}><span style={{ color: '#64748b' }}>Email / Identifiant</span><b>{account.login}</b></div>
          <div style={row}><span style={{ color: '#64748b' }}>Statut</span><b>{ETAT_LABEL[account.etat] || account.etat}</b></div>
          <div style={row}><span style={{ color: '#64748b' }}>Connexion</span><b>{account.last_login_at ? `Déjà connecté` : 'Jamais connecté'}</b></div>
          <div style={{ ...row, borderBottom: 'none' }}><span style={{ color: '#64748b' }}>Dernière connexion</span><b>{fmt(account.last_login_at) || '—'}</b></div>
        </div>

        {resetCreds && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 14 }}>
            Nouveau mot de passe temporaire : <code style={{ background: '#fff', padding: '2px 6px', borderRadius: 4 }}>{resetCreds.temp_password}</code>
            <div style={{ fontSize: 12, color: '#1e3a8a', marginTop: 6 }}>
              {resetCreds.email?.sent
                ? (resetCreds.email.mode === 'sandbox'
                  ? `⚠️ Mode test : l'email (identifiant + mot de passe) a été envoyé à votre adresse ${resetCreds.email.to}.`
                  : `Email envoyé à ${resetCreds.email.to}.`)
                : 'Email non envoyé — transmets ce mot de passe manuellement.'}
            </div>
          </div>
        )}

        <label style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>Actions
          <select value={action} onChange={(e) => { setAction(e.target.value); setErr(null); }} style={sel}>
            <option value="">— Choisir une action —</option>
            {opts.map((a) => <option key={a} value={a}>{ACTIONS[a].label}</option>)}
          </select>
        </label>

        {action && (
          <div style={{ marginTop: 12 }}>
            <p style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: 10, color: '#9a3412', fontSize: 13, margin: 0 }}>⚠️ {ACTIONS[action].warn}</p>
            <label style={{ fontSize: 13, color: '#475569', display: 'block', marginTop: 10 }}>Commentaire (optionnel)
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} style={{ ...sel, fontFamily: 'inherit' }} placeholder="Motif / précision…" />
            </label>
            {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '8px 0 0' }}>{err}</p>}
            <button onClick={confirm} disabled={busy} style={{ marginTop: 12, width: '100%', padding: 12, borderRadius: 10, border: 'none', background: ACTIONS[action].color, color: 'white', fontWeight: 700, fontSize: 14, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
              {busy ? 'En cours…' : `Confirmer : ${ACTIONS[action].label}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
